import { clearAdminSession, getAdminToken } from '../hooks/useAdminAuth';

const API_URL = import.meta.env.VITE_API_URL;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface AdminApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
}

/**
 * Minimal fetch wrapper for `/api/admin/**` calls, attaching `Authorization: Bearer <adminToken>`
 * from `useAdminAuth`'s store. SF-112: has no real consumers yet beyond login/me — SF-113+ wires
 * this into the actual Universe/World/Age data calls once Chancellery moves off mock data.
 */
async function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<AdminApiResponse<T>> {
  const token = getAdminToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      // Admin sessions are in-memory only with no refresh flow (SF-112) — a 401 here always means
      // the token is gone (expired, invalid, or never set). Clear it so RequireAdminAuth redirects
      // to /chancellery/login on the next check.
      clearAdminSession();
    }

    const responseBody = await response.json().catch(() => null);

    if (!response.ok) {
      return { data: null, error: responseBody?.message ?? 'Request failed', status: response.status };
    }

    return { data: responseBody as T, error: null, status: response.status };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Network error', status: 0 };
  }
}

export const adminApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export interface AdminLoginResponse {
  accessToken: string;
  username: string;
  expiresAt: string;
}

export async function adminLogin(username: string, password: string): Promise<AdminApiResponse<AdminLoginResponse>> {
  return request<AdminLoginResponse>('POST', '/api/admin/auth/login', { username, password });
}

export interface AdminMeResponse {
  username: string;
}

export async function adminMe(): Promise<AdminApiResponse<AdminMeResponse>> {
  return request<AdminMeResponse>('GET', '/api/admin/auth/me');
}
